/* eslint-disable react/require-default-props */
import SquareLoader from 'components/atoms/square_loader';
import React, { useState, useEffect, useRef } from 'react';
import { AudioWaveRecorder } from 'components/atoms/audio_wave';
import { useAudioRecorderController } from 'hooks/useAudioRecorderController';
import createTrack from 'services/actions/tracker_actions';
import { speechToText } from 'services/actions/chatbot_actions';
import { Content } from './styles';

interface CommentInputProps {
  isLoading: boolean;
  createComment: (comment: string, commentSource: string) => void;
  commentSource?: string;
  variant?: 'dark' | 'white';
}

const CommentInput = (props: CommentInputProps) => {
  const { isLoading, createComment, commentSource = '', variant } = props;
  const [comment, setComment] = useState<string>('');
  const componentRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const { waveRef, isRecording, startRecording, cancelRecording, stopRecording } =
    useAudioRecorderController();
  const [transcribing, setTranscribing] = useState(false);

  const isAudioBusy = isRecording || transcribing;

  const createInputRef = () => {
    if (!componentRef) return;

    componentRef.current.addEventListener('input', (event) => {
      setComment((event.target as HTMLInputElement)?.innerText);
    });
  };

  const handlePaste = (event: any) => {
    event.preventDefault();
    document.execCommand('inserttext', false, event.clipboardData?.getData('text/plain'));
  };

  useEffect(() => {
    createInputRef();
  }, [componentRef]);

  const handleFinish = async (blob: Blob) => {
    const formData = new FormData();
    formData.append('audio', blob, 'audio.webm');

    try {
      setTranscribing(true);
      createTrack('Anotações - Ditar', { fonte: commentSource });

      const response = await speechToText(formData);

      if (response.status === 200) {
        if (componentRef.current) {
          componentRef.current.innerText = response.data?.text || '';
          componentRef.current.focus();
        }
      }
    } catch (error) {
      console.error('Erro na transcrição:', error);
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <>
      {isLoading && (
        <div className="notes-loading d-flex justify-content-center mb-2">
          <div className="position-relative pr-4">
            Carregando <SquareLoader customClass="create-comment-loading" />
          </div>
        </div>
      )}
      <Content className="notes-input-group">
        <div
          aria-label="Digite uma mensagem"
          tabIndex={0}
          role="textbox"
          className={`notes-input ${comment ? 'hide-content' : ''} ${isRecording ? 'd-none' : ''}`}
          contentEditable={!isLoading}
          onPaste={(event) => {
            handlePaste(event as any);
          }}
          ref={componentRef}
          onKeyDown={(e) => {
            createInputRef();
            if (comment && e.key === 'Enter' && !e.shiftKey) {
              createComment(comment, commentSource);
              setComment('');
              componentRef.current.innerText = '';
            }
          }}
        />
        {isRecording && (
          <div className="notes-input-wave px-2 flex-grow-1">
            <AudioWaveRecorder ref={waveRef} variant={variant} />
          </div>
        )}
        <div className="notes-input-audio d-flex align-items-center">
          {isAudioBusy && (
            <div className="notes-input-audio-control d-flex">
              <button
                type="button"
                className="btn d-flex align-items-center p-0 notes-audio-cancel"
                onClick={cancelRecording}
                disabled={transcribing}
              >
                <span className="material-icons">close</span>
              </button>
              <button
                type="button"
                className="btn d-flex align-items-center p-0 notes-audio-submit ml-1"
                onClick={() => stopRecording(handleFinish)}
                disabled={transcribing}
              >
                {transcribing ? (
                  <div className="notes-audio-equalizer-bars">
                    <span />
                    <span />
                    <span />
                  </div>
                ) : (
                  <span className="material-icons">check</span>
                )}
              </button>
            </div>
          )}
          {!isRecording && (
            <button
              type="button"
              className="btn btn-notes-audio d-flex align-items-center p-0"
              onClick={startRecording}
            >
              <span className="material-icons">mic</span>
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn btn-input-send d-flex align-items-center ml-1"
          onClick={() => {
            createInputRef();
            if (comment) {
              createComment(comment, commentSource);
              setComment('');
              componentRef.current.innerText = '';
            }
          }}
          disabled={isLoading}
        >
          <span className="material-icons">send</span>
        </button>
      </Content>
    </>
  );
};

export default CommentInput;
